import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeftRight,
  Ban,
  Download,
  FileText,
  Mail,
  Package2,
  Printer,
  RefreshCw,
  RotateCcw,
  Trash2,
} from "lucide-react";
import React from "react";
import toast from "react-hot-toast";
import { Button } from "../../components/ui/Button";
import { useBlacklist } from "../../context/BlacklistContext";
import { useIpBlacklist } from "../../context/IpBlacklistContext";
import { fetchWithAuth } from "../../hooks/useAuth";
import { OrderStatus } from "../../prisma-types";

type OrderHeaderOrder = {
  id: number;
  status: OrderStatus;
  awbNumber: string | null;
  invoiceNumber: string | null;
  invoiceSeries: string | null;
  invoiceUrl: string | null;
  shippingStatus: string;
  clientIp?: string;
  payment?: {
    method: "NETOPIA" | "RAMBURS";
  };
  metadata?: {
    awbPrinted?: boolean;
    awbPrintedAt?: string;
  } & Record<string, any>;
};

interface OrderHeaderActionsProps {
  order: OrderHeaderOrder;
  id: string;
}

function OrderHeaderActions({ order, id }: OrderHeaderActionsProps) {
  const queryClient = useQueryClient();
  const { add: addIp } = useIpBlacklist();
  const {
    add: addPhone,
    remove: removePhone,
    isPhoneBlacklisted,
  } = useBlacklist();

  const customerPhone = (order.metadata?.customerPhone as string) || "";
  const customerName = (
    (order.metadata?.customerName as string) ||
    (order.metadata?.name as string) ||
    ""
  ).trim();
  const [isBlacklisted, setIsBlacklisted] = React.useState<boolean | null>(
    null
  );
  const [isUpdatingBlacklist, setIsUpdatingBlacklist] = React.useState(false);

  React.useEffect(() => {
    let isMounted = true;
    (async () => {
      if (!customerPhone) {
        if (isMounted) setIsBlacklisted(null);
        return;
      }
      try {
        const inList = await isPhoneBlacklisted(customerPhone);
        if (isMounted) setIsBlacklisted(inList);
      } catch {
        if (isMounted) setIsBlacklisted(null);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [customerPhone, isPhoneBlacklisted]);

  return (
    <div className="flex justify-between items-center mb-8 bg-white p-4 rounded-lg shadow-sm">
      <div className="flex items-center gap-2">
        <Package2 className="w-8 h-8 text-red-600" />
        <h1 className="text-2xl font-bold text-gray-900">
          Comanda #{order.id}
        </h1>
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          onClick={() =>
            queryClient.invalidateQueries({
              queryKey: ["order", id],
              refetchType: "active",
            })
          }
          className="flex items-center gap-2 hover:bg-gray-100"
        >
          <RefreshCw className="w-4 h-4" />
          Actualizează
        </Button>

        {order.clientIp && (
          <Button
            variant="outline"
            className="flex items-center gap-2 text-red-700 border-red-200 hover:bg-red-50"
            onClick={async () => {
              if (
                !window.confirm(
                  `Blochezi IP-ul ${order.clientIp}? Această acțiune va adăuga IP-ul în blacklist.`
                )
              ) {
                return;
              }
              try {
                const ip = order.clientIp as string;
                await addIp(ip, "Comanda #" + order.id);
                toast.success("IP adăugat în blacklist");
              } catch (error) {
                toast.error(
                  error instanceof Error
                    ? error.message
                    : "Eroare la adăugarea IP-ului în blacklist"
                );
              }
            }}
          >
            <Ban className="w-4 h-4" />
            Blochează IP
          </Button>
        )}

        {/* Toggle phone blacklist (add/remove based on status) */}
        <Button
          variant="outline"
          className={`flex items-center gap-2 ${
            isBlacklisted
              ? "text-green-700 border-green-200 hover:bg-green-50"
              : "text-red-700 border-red-200 hover:bg-red-50"
          }`}
          disabled={!customerPhone || isUpdatingBlacklist}
          onClick={async () => {
            if (!customerPhone) {
              toast.error("Număr de telefon indisponibil");
              return;
            }
            try {
              setIsUpdatingBlacklist(true);
              if (isBlacklisted) {
                const reason = customerName
                  ? `scoatere de admin - ${customerName}`
                  : "scoatere de admin";
                await removePhone(customerPhone, reason);
                setIsBlacklisted(false);
                toast.success("Numărul a fost scos din blacklist");
              } else {
                const reason = customerName
                  ? `adăugat de admin - ${customerName}`
                  : "adăugat de admin";
                await addPhone(customerPhone, reason);
                setIsBlacklisted(true);
                toast.success("Numărul a fost adăugat în blacklist");
              }
            } catch (error) {
              toast.error(
                error instanceof Error
                  ? error.message
                  : "Eroare la actualizarea blacklist-ului"
              );
            } finally {
              setIsUpdatingBlacklist(false);
            }
          }}
        >
          <Ban className="w-4 h-4" />
          {isBlacklisted === null
            ? "Verificare blacklist..."
            : isBlacklisted
            ? "Scoate din blacklist"
            : "Adaugă telefon în blacklist"}
        </Button>

        {order.payment?.method === "NETOPIA" &&
          order.status !== OrderStatus.CANCELLED && (
            <Button
              variant="outline"
              className="flex items-center gap-2 bg-gray-100 text-gray-800 hover:bg-gray-200"
              onClick={async () => {
                try {
                  const response = await fetchWithAuth(
                    `/api/admin/orders/${order.id}/convert-to-ramburs`,
                    {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ generateDocuments: true }),
                    }
                  );
                  if (!response.ok) {
                    const err = await response.json().catch(() => ({} as any));
                    throw new Error(
                      err?.message || "Eroare la conversia comenzii la RAMBURS"
                    );
                  }

                  await Promise.all([
                    queryClient.invalidateQueries({ queryKey: ["orders"] }),
                    queryClient.invalidateQueries({ queryKey: ["order", id] }),
                  ]);

                  toast.success("Comanda a fost convertită la RAMBURS");
                } catch (error) {
                  toast.error(
                    error instanceof Error
                      ? error.message
                      : "Eroare la conversia comenzii la RAMBURS"
                  );
                }
              }}
            >
              <ArrowLeftRight className="w-4 h-4" />
              Convertește la RAMBURS
            </Button>
          )}

        {order.payment?.method === "NETOPIA" && (
          <Button
            variant="outline"
            className="flex items-center gap-2 hover:bg-gray-100"
            onClick={async () => {
              try {
                const response = await fetchWithAuth(
                  `/api/admin/payments/orders/${order.id}/retry-email`,
                  { method: "POST" }
                );
                if (!response.ok) {
                  const error = await response.json().catch(() => ({} as any));
                  throw new Error(
                    (error && (error.message || error.error)) ||
                      "Eroare la retrimiterea linkului de plată"
                  );
                }
                toast.success("Linkul de plată a fost retrimis");
              } catch (error) {
                toast.error(
                  error instanceof Error
                    ? error.message
                    : "Eroare la retrimiterea linkului de plată"
                );
              }
            }}
          >
            <Mail className="w-4 h-4" />
            Retrimite linkul de plată
          </Button>
        )}

        {order.awbNumber && (
          <>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="flex items-center gap-2 hover:bg-gray-100"
                onClick={async () => {
                  try {
                    const response = await fetchWithAuth(
                      `/api/livrare/documents/${order.id}/download/awb`,
                      { method: "GET" }
                    );
                    if (!response.ok)
                      throw new Error("Eroare la descărcarea AWB");

                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);

                    const newWindow = window.open(url, "_blank");
                    if (!newWindow) {
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `AWB-${order.awbNumber}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                    }

                    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                  } catch (error) {
                    toast.error("Eroare la descărcarea AWB");
                  }
                }}
              >
                <Download className="w-4 h-4" />
                Descarcă AWB: {order.awbNumber}
              </Button>

              <Button
                variant="outline"
                className={`flex items-center gap-2 rounded-full px-4 shadow-sm transition-all duration-200 ${
                  order.metadata?.awbPrinted
                    ? "bg-green-500 text-white hover:bg-green-600 ring-1 ring-green-400"
                    : "bg-yellow-100 text-yellow-800 hover:bg-yellow-200 ring-1 ring-yellow-200"
                }`}
                disabled={order.metadata?.awbPrinted}
                onClick={async () => {
                  try {
                    const response = await fetchWithAuth(
                      `/api/livrare/documents/${order.id}/download/awb`,
                      { method: "GET" }
                    );
                    if (!response.ok)
                      throw new Error("Eroare la printarea AWB");

                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);

                    let printFrame: HTMLIFrameElement | null = null;
                    try {
                      const resp = await fetchWithAuth(
                        `/api/admin/orders/${order.id}/mark-awb-printed`,
                        { method: "POST" }
                      );
                      if (!resp.ok)
                        throw new Error(
                          "Eroare la actualizarea statusului AWB"
                        );

                      const updatedOrder = await resp.json();
                      const newData = {
                        ...(order || {}),
                        ...updatedOrder,
                        metadata: {
                          ...(order?.metadata || {}),
                          ...updatedOrder.metadata,
                          awbPrinted: true,
                          awbPrintedAt: new Date().toISOString(),
                        },
                      };
                      queryClient.setQueryData(["order", id], newData);

                      // If shipping email wasn't sent yet, trigger it concurrently
                      if (!order.metadata?.shippingEmailSent) {
                        (async () => {
                          try {
                            const emailResponse = await fetchWithAuth(
                              `/api/admin/orders/${order.id}/send-shipping-email`,
                              { method: "POST" }
                            );
                            if (!emailResponse.ok) {
                              throw new Error(
                                "Eroare la trimiterea email-ului"
                              );
                            }
                            const metadataResponse = await fetchWithAuth(
                              `/api/admin/orders/${order.id}/metadata`,
                              {
                                method: "PATCH",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                  shippingEmailSent: true,
                                }),
                              }
                            );
                            if (!metadataResponse.ok) {
                              throw new Error(
                                "Eroare la actualizarea statusului emailului"
                              );
                            }
                            await Promise.all([
                              queryClient.invalidateQueries({
                                queryKey: ["orders"],
                              }),
                              queryClient.invalidateQueries({
                                queryKey: ["order", id],
                              }),
                            ]);
                            toast.success("Email trimis cu succes");
                          } catch (error) {
                            toast.error("Eroare la trimiterea email-ului");
                          }
                        })();
                      }

                      const printWindow = window.open(url, "_blank");
                      if (printWindow) {
                        printWindow.onload = () => {
                          try {
                            printWindow.print();
                            const checkPrintDialog = setInterval(() => {
                              if (!document.hasFocus()) return;
                              clearInterval(checkPrintDialog);
                              printWindow.close();
                            }, 1000);
                          } catch (error) {
                            console.error("Print error:", error);
                            printWindow.close();
                          }
                        };
                      } else {
                        printFrame = document.createElement("iframe");
                        printFrame.style.cssText =
                          "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
                        printFrame.src = url;
                        document.body.appendChild(printFrame);

                        setTimeout(() => {
                          try {
                            if (printFrame?.contentWindow) {
                              printFrame.contentWindow.print();
                            }
                          } catch (error) {
                            console.error("Print error:", error);
                          } finally {
                            setTimeout(() => {
                              if (
                                printFrame &&
                                document.body.contains(printFrame)
                              ) {
                                document.body.removeChild(printFrame);
                              }
                            }, 1000);
                          }
                        }, 1000);
                      }
                      toast.success("AWB printat cu succes");
                    } catch (error) {
                      console.error("Error updating AWB status:", error);
                      toast.error("Eroare la actualizarea statusului AWB");
                    } finally {
                      try {
                        if (printFrame && document.body.contains(printFrame)) {
                          document.body.removeChild(printFrame);
                        }
                        window.URL.revokeObjectURL(url);
                      } catch (error) {
                        console.error("Cleanup error:", error);
                      }
                    }
                  } catch (error) {
                    toast.error("Eroare la printarea AWB");
                  }
                }}
              >
                <Printer className="w-4 h-4" />
                {order.metadata?.awbPrinted ? "Printat" : "Printează AWB"}
              </Button>
            </div>

            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const response = await fetchWithAuth(
                    `/api/livrare/awb/${order.awbNumber}`,
                    { method: "DELETE" }
                  );
                  if (!response.ok) throw new Error("Eroare la anularea AWB");

                  await queryClient.invalidateQueries({
                    queryKey: ["order", id],
                  });
                  toast.success("AWB anulat cu succes");
                } catch (error) {
                  toast.error("Eroare la anularea AWB");
                }
              }}
              disabled={order.shippingStatus === "CANCELED"}
            >
              <Ban className="w-4 h-4" />
              Anulează AWB
            </Button>
          </>
        )}

        {order.invoiceNumber && (
          <Button
            variant="outline"
            className="flex items-center gap-2 hover:bg-gray-100"
            onClick={() =>
              order.invoiceUrl && window.open(order.invoiceUrl, "_blank")
            }
          >
            <FileText className="w-4 h-4" />
            Factură: {order.invoiceSeries}/{order.invoiceNumber}
          </Button>
        )}

        {order.status === OrderStatus.CANCELLED ? (
          <Button
            variant="default"
            className="flex items-center gap-2 "
            onClick={async () => {
              if (
                window.confirm(
                  "Sigur doriți să restaurați această comandă anulată?"
                )
              ) {
                try {
                  const response = await fetchWithAuth(
                    `/api/admin/orders/${order.id}/restore`,
                    { method: "POST" }
                  );
                  if (!response.ok) {
                    const err = await response.json().catch(() => ({} as any));
                    throw new Error(
                      err?.message || "Eroare la restaurarea comenzii"
                    );
                  }

                  await Promise.all([
                    queryClient.invalidateQueries({ queryKey: ["orders"] }),
                    queryClient.invalidateQueries({ queryKey: ["order", id] }),
                  ]);

                  toast.success("Comanda a fost restaurată cu succes");
                } catch (error) {
                  toast.error(
                    error instanceof Error
                      ? error.message
                      : "Eroare la restaurarea comenzii"
                  );
                  console.error("Error restoring order:", error);
                }
              }
            }}
          >
            <RotateCcw className="w-4 h-4" />
            Restaurează Comanda
          </Button>
        ) : (
          <Button
            variant="destructive"
            className="flex items-center gap-2"
            onClick={async () => {
              if (
                window.confirm(
                  "Sigur doriți să anulați această comandă? Această acțiune va anula și AWB-ul și factura asociată."
                )
              ) {
                try {
                  const response = await fetchWithAuth(
                    `/api/admin/orders/${order.id}/status`,
                    {
                      method: "PUT",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ status: "CANCELLED" }),
                    }
                  );

                  if (!response.ok) {
                    throw new Error("Eroare la anularea comenzii");
                  }

                  await Promise.all([
                    queryClient.invalidateQueries({ queryKey: ["orders"] }),
                    queryClient.invalidateQueries({ queryKey: ["order", id] }),
                  ]);
                  toast.success("Comanda a fost anulată cu succes");
                } catch (error) {
                  toast.error("Eroare la anularea comenzii");
                  console.error("Error cancelling order:", error);
                }
              }
            }}
          >
            <Trash2 className="w-4 h-4" />
            Anulează Comanda
          </Button>
        )}
      </div>
    </div>
  );
}

export default OrderHeaderActions;
